import * as React from 'react';
import {useCallback, useMemo, useState} from 'react';

import {Alerts} from '../../ui/Alerts/Alerts';
import {FetchService} from '../../../client/fetch/fetch.service';
import {gaLegacyCustomEvent} from '../../../client/ga/ga-legacy.functions';
import {SearchService} from '../../../client/search/search.service';
import {useService} from '../../react/ServiceContext';
import {VehicleSelectionEntity} from '../search.types';
import Button from '../../ui/Buttons/Button';
import {Select} from '../../ui/forms/Select/Select';
import {Spinner} from '../../ui/Spinner/Spinner';

export interface StaticData {
    makes?: VehicleSelectionEntity[];
    models?: VehicleSelectionEntity[];
    selectedMake?: string;
    selectedModel?: string;
    selectedType?: string;
    selectedYear?: string;
    types: VehicleSelectionEntity[];
    years?: VehicleSelectionEntity[];
}

interface VehicleSelectorProps {
    staticData: StaticData;
}

export const VehicleSelector = ({staticData}: VehicleSelectorProps) => {
    const [errorMessage, setErrorMessage] = useState(``);
    const [isLoading, setIsLoading] = useState(false);
    const [makes, setMakes] = useState(staticData.makes);
    const [models, setModels] = useState(staticData.models);
    const [selectedMake, setSelectedMake] = useState(staticData.selectedMake);
    const [selectedModel, setSelectedModel] = useState(staticData.selectedModel);
    const [selectedType, setSelectedType] = useState(staticData.selectedType);
    const [selectedYear, setSelectedYear] = useState(staticData.selectedYear);
    const [years, setYears] = useState(staticData.years);
    const fetchService: FetchService = useService(`fetchService`);
    const searchService = useMemo(() => new SearchService(fetchService), [fetchService]);

    /**
     * TBD
     */
    const getMakeName = (): string => {
        if (!selectedMake) {
            return ``;
        }
        if (!(makes?.length > 0)) {
            return ``;
        }
        let name = ``;
        makes.forEach((vehicleSelectionEntity) => {
            if (vehicleSelectionEntity._id === selectedMake) {
                name = vehicleSelectionEntity.name;
            }
        });
        return name;
    };

    /**
     * TBD
     */
    const getModelName = (): string => {
        if (!selectedModel) {
            return ``;
        }
        if (!(models?.length > 0)) {
            return ``;
        }
        let name = ``;
        models.forEach((vehicleSelectionEntity) => {
            if (vehicleSelectionEntity._id === selectedModel) {
                name = vehicleSelectionEntity.name;
            }
        });
        return name;
    };

    /**
     * TBD
     */
    const getTypeName = (): string => {
        if (!selectedType) {
            return ``;
        }
        if (!(staticData.types?.length > 0)) {
            return ``;
        }
        let name = ``;
        staticData.types.forEach((vehicleSelectionEntity) => {
            if (vehicleSelectionEntity._id === selectedType) {
                name = vehicleSelectionEntity.name;
            }
        });
        return name;
    };

    /**
     * TBD
     */
    const getYearName = (): string => {
        if (!selectedYear) {
            return ``;
        }
        if (!(years?.length > 0)) {
            return ``;
        }
        let name = ``;
        years.forEach((vehicleSelectionEntity) => {
            if (vehicleSelectionEntity._id === selectedYear) {
                name = vehicleSelectionEntity.name;
            }
        });
        return name;
    };

    /**
     * TBD
     */
    const loadMakes = useCallback(
        (newType: string) => {
            setIsLoading(true);
            searchService
                .vehicleSelector(newType)
                .then((loadedMakes) => {
                    setMakes(loadedMakes);
                    setModels(undefined);
                    setYears(undefined);
                    setSelectedYear(undefined);
                    setSelectedModel(undefined);
                    setSelectedMake(undefined);
                    setIsLoading(false);
                })
                .catch((loadError) => {
                    setIsLoading(false);
                    setErrorMessage(loadError.message);
                });
        },
        [searchService],
    );

    /**
     * TBD
     */
    const loadModels = useCallback(
        (newMake: string) => {
            setIsLoading(true);
            searchService
                .vehicleSelector(selectedType, newMake)
                .then((loadedModels) => {
                    setModels(loadedModels);
                    setYears(undefined);
                    setSelectedYear(undefined);
                    setSelectedModel(undefined);
                    setIsLoading(false);
                })
                .catch((loadError) => {
                    setIsLoading(false);
                    setErrorMessage(loadError.message);
                });
        },
        [selectedType, searchService],
    );

    /**
     * TBD
     */
    const loadYears = useCallback(
        (newModel: string) => {
            setIsLoading(true);
            searchService
                .vehicleSelector(selectedType, selectedMake, newModel)
                .then((loadedYears) => {
                    setYears(loadedYears);
                    setSelectedYear(undefined);
                    setIsLoading(false);
                })
                .catch((loadError) => {
                    setIsLoading(false);
                    setErrorMessage(loadError.message);
                });
        },
        [searchService, selectedType, selectedMake],
    );

    /**
     * Send GA and navigate to selected options
     */
    const navigate = () => {
        gaLegacyCustomEvent({
            eventAction: `Wiper Blades | ${window.location.pathname}` as any,
            eventCategory: `Vehicle Selector`,
            eventLabel: `Type: ${getTypeName()} | Year: ${getYearName()} | Make: ${getMakeName()} | Model: ${getModelName()}`,
        });
        window.location.replace(`/wiper-blade-finder/${selectedYear}`);
    };

    const getYearOptions = () => {
        const yearOptions = [{key: '', value: `Year`}];
        if (years?.length > 0)
            years.forEach((vehicleSelectionEntity) => {
                yearOptions.push({key: vehicleSelectionEntity.slug, value: vehicleSelectionEntity.name});
            });
        return yearOptions;
    };

    const getModelOptions = () => {
        const modelOptions = [{key: '', value: `Model`}];
        if (models?.length > 0)
            models.forEach((vehicleSelectionEntity) => {
                modelOptions.push({key: vehicleSelectionEntity._id, value: vehicleSelectionEntity.name});
            });
        return modelOptions;
    };

    const getMakeOptions = () => {
        const makeOptions = [{key: '', value: `Make`}];
        if (makes?.length > 0)
            makes.forEach((vehicleSelectionEntity) => {
                makeOptions.push({key: vehicleSelectionEntity._id, value: vehicleSelectionEntity.name});
            });
        return makeOptions;
    };

    const getTypeOptions = () => {
        const typeOptions = [{key: '', value: `Vehicle Type`}];
        if (staticData.types?.length > 0)
            staticData.types.forEach((vehicleSelectionEntity) => {
                typeOptions.push({key: vehicleSelectionEntity._id, value: vehicleSelectionEntity.name});
            });
        return typeOptions;
    };

    /**
     * Template
     */
    return (
        <div data-e2e="VehicleSelector">
            <Alerts
                message={errorMessage}
                variant="danger"
            />
            <div className="tw-relative tw-bg-gray-250 mb-4 pt-2 pr-2 pl-2">
                {isLoading && (
                    <div className="tw-absolute tw-pt-[35px] tw-top-0 tw-left-0 tw-w-full tw-h-full tw-z-[1021] tw-bg-gray-700 tw-opacity-50">
                        <Spinner className="tw-my-[-35px] tw-mx-auto tw-top-[50%] tw-relative" />
                    </div>
                )}
                <div className="tw-grid md:tw-flex tw-w-full">
                    <Select
                        aria-label="Vehicle Type"
                        className="!tw-mb-0 !tw-flex !tw-w-full"
                        defaultValue={selectedType}
                        id="typeDropdown"
                        label=""
                        name="typeDropdown"
                        onChange={(changeEvent) => {
                            setSelectedType(changeEvent.target.value);
                            loadMakes(changeEvent.target.value);
                        }}
                        options={getTypeOptions()}
                        selectClasses="!tw-h-[calc(1.5em+1rem+6px)] !tw-py-2 !tw-px-4 !tw-leading-[1.5] !tw-rounded-[0.2rem] !tw-text-xl !tw-w-full !tw-mr-0 md:!tw-mr-2 !tw-mb-2"
                    />
                    <Select
                        aria-label="Make"
                        className="!tw-mb-0 !tw-flex !tw-w-full"
                        defaultValue={selectedMake}
                        disabled={!selectedType}
                        id="makeDropdown"
                        label=""
                        name="makeDropdown"
                        onChange={(changeEvent) => {
                            setSelectedMake(changeEvent.target.value);
                            loadModels(changeEvent.target.value);
                        }}
                        options={getMakeOptions()}
                        selectClasses="!tw-h-[calc(1.5em+1rem+6px)] !tw-py-2 !tw-px-4 !tw-leading-[1.5] !tw-rounded-[0.2rem] !tw-text-xl !tw-w-full !tw-mr-0 md:!tw-mr-2 !tw-mb-2"
                    />
                    <Select
                        aria-label="Model"
                        className="!tw-mb-0 !tw-flex !tw-w-full"
                        defaultValue={selectedModel}
                        disabled={!selectedMake}
                        id="modelDropdown"
                        label=""
                        name="modelDropdown"
                        onChange={(changeEvent) => {
                            setSelectedModel(changeEvent.target.value);
                            loadYears(changeEvent.target.value);
                        }}
                        options={getModelOptions()}
                        selectClasses="!tw-h-[calc(1.5em+1rem+6px)] !tw-py-2 !tw-px-4 !tw-leading-[1.5] !tw-rounded-[0.2rem] !tw-text-xl !tw-w-full !tw-mr-0 md:!tw-mr-2 !tw-mb-2"
                    />
                    <Select
                        aria-label="Year"
                        className="!tw-mb-0 !tw-flex !tw-w-full"
                        defaultValue={selectedYear}
                        disabled={!selectedModel}
                        id="yearDropdown"
                        label=""
                        name="yearDropdown"
                        onChange={(changeEvent) => {
                            setSelectedYear(changeEvent.target.value);
                        }}
                        options={getYearOptions()}
                        selectClasses="!tw-h-[calc(1.5em+1rem+6px)] !tw-py-2 !tw-px-4 !tw-leading-[1.5] !tw-rounded-[0.2rem] !tw-text-xl !tw-w-full !tw-mr-0 md:!tw-mr-2 !tw-mb-2"
                    />
                    <Button
                        className="!tw-sticky tw-top-0 !tw-w-full tw-whitespace-nowrap tw-mb-2 md:!tw-w-[159px]"
                        disabled={!selectedYear}
                        onClick={navigate}
                        size="lg"
                        variant="secondary"
                    >
                        Select Vehicle
                    </Button>
                </div>
            </div>
        </div>
    );
};
